import { useState, useEffect } from "react";
import { Bell, Check, CheckCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

const NotificationBell = () => {
  const { user, roles } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: notifications } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Realtime subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("user-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  const unreadCount = (notifications || []).filter((n: any) => !n.is_read).length;

  const markAsRead = async (id: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
  };

  const handleNotificationClick = async (n: any) => {
    if (!n.is_read) await markAsRead(n.id);
    setOpen(false);
    // Navigate based on notification type
    if (n.reference_id) {
      switch (n.type) {
        case "new_lesson":
        case "lesson_explanation":
          navigate(`/dashboard/lesson/${n.reference_id}`);
          break;
        case "new_exam":
          navigate(`/dashboard/take-exam/${n.reference_id}`);
          break;
        case "new_assignment":
        case "assignment_graded":
          navigate("/dashboard/parent");
          break;
        case "live_session":
          navigate("/dashboard/parent");
          break;
        case "new_invoice":
          navigate(roles.includes("parent") && !roles.includes("admin") ? "/dashboard/parent-home" : "/dashboard/billing");
          break;
        case "subscription_invoice":
          navigate(roles.includes("parent") && !roles.includes("admin") ? "/dashboard/parent-home" : "/dashboard/billing");
          break;
        case "negative_observation":
          navigate("/dashboard/parent");
          break;
        case "admin_new_nursery":
          navigate("/admin/nurseries");
          break;
        case "admin_subscription_change":
        case "admin_subscription_request":
          navigate("/admin/subscriptions");
          break;
        case "admin_new_ticket":
          navigate("/admin/support");
          break;
        case "admin_new_user":
          navigate("/admin/users");
          break;
        default:
          break;
      }
    }
  };

  const markAllRead = async () => {
    if (!user) return;
    await supabase.from("notifications").update({ is_read: true }).eq("user_id", user.id).eq("is_read", false);
    queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
  };

  const typeIcon = (type: string) => {
    switch (type) {
      case "new_exam": return "📝";
      case "exam_result": return "🏆";
      case "support_ticket": return "🎫";
      case "support_urgent": return "🚨";
      case "ticket_assigned": return "📌";
      case "assignment_graded": return "✅";
      case "child_linked": return "👶";
      case "new_assignment": return "📋";
      case "live_session": return "📺";
      case "new_lesson": return "📖";
      case "lesson_explanation": return "🎬";
      case "new_invoice": return "🧾";
      case "subscription_invoice": return "🧾";
      case "negative_observation": return "⚠️";
      case "admin_new_nursery": return "🏫";
      case "admin_subscription_change": return "💳";
      case "admin_new_ticket": return "🎫";
      case "admin_new_user": return "👤";
      case "admin_subscription_request": return "📩";
      case "broadcast": return "📢";
      default: return "🔔";
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-destructive text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" dir="rtl">
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold text-sm">الإشعارات</h4>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={markAllRead}>
              <CheckCheck className="w-3 h-3" />
              قراءة الكل
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {(notifications || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">لا توجد إشعارات</p>
          ) : (
            <div className="divide-y">
              {(notifications || []).map((n: any) => (
                <div
                  key={n.id}
                  className={`p-3 flex gap-3 cursor-pointer hover:bg-muted/50 transition-colors ${!n.is_read ? "bg-primary/5" : ""}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <span className="text-lg shrink-0 mt-0.5">{typeIcon(n.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {format(new Date(n.created_at), "d MMM yyyy HH:mm", { locale: ar })}
                    </p>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
